import styled from 'styled-components';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { isNumberZero } from 'utils/numberHelpers';
import { maybeLocalizeCurrencyValue, maybeLocalizeInteger } from 'utils/i18nHelpers';
import { InsightsSection } from './InsightsSection';
import { InsightsTimeframePicker } from './InsightsTimeframePicker';
import { NumberInsightPanel } from './NumberInsightPanel';
import { UnsubscribeRatePanel } from './UnsubscribeRatePanel';
import { ConversionRatePanel } from './ConversionRatePanel';
import { AUDIENCE_INSIGHTS_HIGHEST_CAMPAIGNS_FEATURE_FLAG } from '../featureFlags';
import { useTranslation } from '../hooks';
import { InsightType } from '../domain';
import { BehavioralEventSelect } from './BehavioralEventSelect';
import { CustomBehavioralInsightsSection } from './CustomBehaviorialInsightsSection';
import { useConversionInsightsContext } from './contexts/ConversionInsightsContext';
import { useBehaviorQueryContext } from './contexts/BehaviorQueryContext';

const ToolbarWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
`;

const getConversionRateData = (
  isEnabled: boolean,
  conversionData: { conversionMetric?: number; isLoading: boolean },
  featureFlaggedConversionData: { conversionMetric: number; isLoading?: boolean },
) => (isEnabled ? featureFlaggedConversionData : conversionData);

export const BehavioralInsightsSection = () => {
  const isHighestConvertingCampaignsEnabled = useFeatureFlag(
    AUDIENCE_INSIGHTS_HIGHEST_CAMPAIGNS_FEATURE_FLAG,
  );

  const { t } = useTranslation();
  const {
    avgOrderValue,
    uniquePurchases,
    revenue,
    totalPurchases,
    totalSent,
    conversionRate,
    totalUnsubscribes,
    unsubscribeRate,
    isBehaviorLoading,
  } = useBehaviorQueryContext();
  const { overallConversionRate, isLoading, highestConvertingCampaigns } =
    useConversionInsightsContext();

  const toolbar = (
    <ToolbarWrapper>
      <InsightsTimeframePicker />
      <BehavioralEventSelect />
    </ToolbarWrapper>
  );

  const localizedTotalPurchases = maybeLocalizeInteger(totalPurchases?.value);
  const localizedUniquePurchases = maybeLocalizeInteger(uniquePurchases?.value);
  const localizedRevenueValue = maybeLocalizeCurrencyValue(revenue?.value);
  const localizedAvgOrderValue = maybeLocalizeCurrencyValue(avgOrderValue?.value);

  const { conversionMetric, isLoading: conversionRateLoading } = getConversionRateData(
    isHighestConvertingCampaignsEnabled,
    { conversionMetric: conversionRate?.value, isLoading: false },
    { conversionMetric: overallConversionRate, isLoading },
  );

  return (
    <InsightsSection type={InsightType.behavioral} isLoading={isBehaviorLoading} toolbar={toolbar}>
      <NumberInsightPanel
        title={t('insights.totalPurchases')}
        value={localizedTotalPurchases}
        isZero={isNumberZero(totalPurchases?.value)}
      />
      <NumberInsightPanel
        title={t('insights.uniquePurchases')}
        value={localizedUniquePurchases}
        isZero={isNumberZero(uniquePurchases?.value)}
      />
      <NumberInsightPanel
        title={t('insights.revenue')}
        value={localizedRevenueValue}
        isZero={isNumberZero(revenue?.value)}
      />
      <NumberInsightPanel
        title={t('insights.avgOrderValue')}
        value={localizedAvgOrderValue}
        isZero={isNumberZero(avgOrderValue?.value)}
      />
      <CustomBehavioralInsightsSection />
      <ConversionRatePanel
        title={t('insights.conversionRate')}
        percentage={conversionMetric}
        isLoading={conversionRateLoading}
        isEmpty={highestConvertingCampaigns.length === 0}
        showSelect
      />
      <UnsubscribeRatePanel
        title={t('insights.unsubscribeRate')}
        percentage={unsubscribeRate?.value}
        sent={totalSent?.value}
        unsubscribed={totalUnsubscribes?.value}
      />
    </InsightsSection>
  );
};
